import React from 'react'
import { connect } from 'react-redux'
import { Field, reduxForm, change } from 'redux-form'
import { bindActionCreators } from 'redux'
import { SubmissionError } from 'redux-form'
import ReactCountryFlag from 'react-country-flag'
import LoadingProgress from '../components/LoadingProgress'

import { fetchRemoteActions, addRemoteAction } from '../actions/remoteactions'
import { renderInputField, renderSelectField } from '../utils/forms'
import PHONE_PREFIXES from '../utils/phonePrefixes'

const ACTION_META = {
  SINGLE_DOOR_OPEN: {
    label: 'Abrir locker',
    iconClass: 'fas fa-door-open',
    description: 'Se abrirá remotamente la puerta del locker.',
    confirmText: 'Confirmar apertura',
    accentColor: '#10b981',
    bgGradient: 'linear-gradient(135deg, #065f46 0%, #047857 100%)',
    warning: null,
  },
  SINGLE_UNRENT: {
    label: 'Desalquilar',
    iconClass: 'fas fa-sign-out-alt',
    description: 'El locker quedará libre y disponible para nuevos alquileres.',
    confirmText: 'Confirmar desalquiler',
    accentColor: '#ef4444',
    bgGradient: 'linear-gradient(135deg, #991b1b 0%, #b91c1c 100%)',
    warning: 'Esta acción liberará el locker. El cliente perderá el acceso inmediatamente.',
  },
  SINGLE_BLOCKLATER: {
    label: 'Marcar QR / PDA',
    iconClass: 'fas fa-qrcode',
    description: 'El locker quedará reservado para acceso mediante código QR o terminal PDA.',
    confirmText: 'Confirmar reserva',
    accentColor: '#8b5cf6',
    bgGradient: 'linear-gradient(135deg, #4c1d95 0%, #6d28d9 100%)',
    warning: null,
  },
  SINGLE_RESET_PIN: {
    label: 'Cambiar PIN',
    iconClass: 'fas fa-lock',
    description: 'Se restablecerá el PIN del locker.',
    confirmText: 'Confirmar cambio de PIN',
    accentColor: '#f59e0b',
    bgGradient: 'linear-gradient(135deg, #92400e 0%, #b45309 100%)',
    warning: null,
  },
  /* Los dos alquileres remotos. Dejan el locker en RM con el dato del cliente, pero NO son
     una venta: no generan factura, ni arqueo, ni ticket. El dato del cliente no cabe en la
     configuración de la máquina (es por locker), así que viaja en arguments junto a la
     matrícula como "<matricula>|<dato>". */
  SINGLE_RENT_PIN: {
    label: 'Alquilar PIN',
    iconClass: 'fas fa-mobile-alt',
    description: 'El locker quedará alquilado a este teléfono. El cliente lo abrirá desde el kiosco con su número; el PIN es el que ya tiene la cerradura.',
    confirmText: 'Confirmar alquiler',
    accentColor: '#ffa600',
    bgGradient: 'linear-gradient(135deg, #ffa600 0%, #c78201 100%)',
    warning: 'No genera venta ni factura: el alquiler no aparecerá en el arqueo.',
    phoneInput: true,
    requiresMode: 'PIN',
  },
  SINGLE_RENT_RFID: {
    label: 'Alquilar RFID',
    iconClass: 'fas fa-id-card',
    description: 'Se grabará el UUID de la pulsera en la cerradura y, solo si la cerradura confirma, el locker quedará alquilado.',
    confirmText: 'Confirmar alquiler',
    accentColor: '#00aeff',
    bgGradient: 'linear-gradient(135deg, #00aeff 0%, #0369a1 100%)',
    warning: 'La cerradura borra su lista de clientes y queda únicamente con este UUID. No genera venta ni factura.',
    uuidInput: true,
    requiresMode: 'RFID',
  },
  SINGLE_NIGHTSCAN: {
    label: 'Vincular',
    iconClass: 'fas fa-globe',
    description: 'Se realizará un nightscan individual para vincular el locker.',
    confirmText: 'Confirmar vinculación',
    accentColor: '#dc2626',
    bgGradient: 'linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%)',
    warning: 'Acción crítica. El locker realizará un ciclo de vinculación completo.',
  },
  SINGLE_KERONG_PROGRAM_OTA: {
    label: 'Program OTA',
    iconClass: 'fas fa-microchip',
    description: 'Actualización del firmware de programa del locker vía OTA.',
    confirmText: 'Confirmar OTA Programa',
    accentColor: '#dc2626',
    bgGradient: 'linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%)',
    warning: 'Acción crítica. El locker quedará fuera de servicio durante la actualización.',
  },
  SINGLE_KERONG_BOOTLOADER_OTA: {
    label: 'Bootloader OTA',
    iconClass: 'fas fa-microchip',
    description: 'Actualización del bootloader del locker vía OTA.',
    confirmText: 'Confirmar Bootloader OTA',
    accentColor: '#dc2626',
    bgGradient: 'linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%)',
    warning: 'Acción muy crítica. No interrumpir el proceso bajo ningún concepto.',
  },
}

const OPERATIVE_ACTIONS = ['SINGLE_DOOR_OPEN', 'SINGLE_UNRENT', 'SINGLE_BLOCKLATER', 'SINGLE_RESET_PIN']
const RENT_ACTIONS = ['SINGLE_RENT_PIN', 'SINGLE_RENT_RFID']
const CRITICAL_ACTIONS = ['SINGLE_NIGHTSCAN', 'SINGLE_KERONG_PROGRAM_OTA', 'SINGLE_KERONG_BOOTLOADER_OTA']

/* Mismo filtro que la app (isValidMasterUuid) y que WristbandWrite.qml: 8 o 14 hex (4 o 7
   bytes). Un UID más largo lo recortaría la cerradura en silencio y luego no abriría. */
const UUID_RE = /^([0-9A-F]{8}|[0-9A-F]{14})$/

/* Lista blanca, la misma que usa el kiosco: AV (libre) y FB (libre con el BLE roto, que se
   sigue alquilando por PIN). Con una lista negra de los estados "alquilada" se colaban los
   que no son ni alquilados ni libres y alquilar encima destruye información: RQ/BL (reserva
   QR) y FL (avería). La máquina repite el control con su matriz, que es la que manda. */
const RENTABLE_STATUSES = ['AV', 'FB']

/* La app corta arguments por '|', así que un '|' dentro del teléfono partiría el mensaje.
   Sólo dígitos, mínimo 6 para no bloquear números extranjeros cortos. */
const PHONE_RE = /^[0-9]{6,15}$/

class RemoteActionSingleAddForm extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      loading: false,
      actionCompleted: false,
      errorMessage: '',
      pendingAction: null,
      cancelled: false,
      showAdvanced: false,
      uuid: '',
      phone: '',
      phonePrefix: '+34',
      prefixOpen: false,
      prefixSearch: '',
      progress: '',
    }

    this.prefixRef = React.createRef()

    this.submit = this.submit.bind(this)
    this.selectAction = this.selectAction.bind(this)
    this.cancelAction = this.cancelAction.bind(this)
    this.setUuid = this.setUuid.bind(this)
    this.setPhone = this.setPhone.bind(this)
    this.handleOutsideClick = this.handleOutsideClick.bind(this)
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleOutsideClick)
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleOutsideClick)
    /* El polling se relanza solo con setTimeout; sin esto seguiría corriendo (y llamando a
       setState) después de que el modal se cierre. */
    this.unmounted = true
  }

  handleOutsideClick(e) {
    if (this.prefixRef.current && !this.prefixRef.current.contains(e.target)) {
      this.setState({ prefixOpen: false, prefixSearch: '' })
    }
  }

  async submit(props) {
    props.machine = this.props.machine._id
    props.executor = this.props.fullname

    this.setState({ loading: true, actionCompleted: false, errorMessage: '', cancelled: false, progress: '' })

    try {
      const response = await this.props.addRemoteAction(props)

      if (response.error) {
        throw new SubmissionError({
          _error: response.error.message,
          ...response.error.fields,
        })
      }

      const action = await this.waitForRemoteActionCompletion(response.response._id)

      /* La máquina distingue COMPLETE de ERROR y explica el motivo en `result` (locker
         ocupado, UID inválido, la cerradura no responde...). Recargar sin mirar el estado
         daba por bueno cualquier fallo. */
      if (action.status === 'ERROR') {
        this.setState({ loading: false, errorMessage: action.result || 'La máquina rechazó la acción' })
        return response
      }

      this.setState({ loading: false, actionCompleted: true })
      window.location.reload()
      return response
    } catch (error) {
      this.setState({ loading: false, errorMessage: error.message || 'Error procesando acción' })
      throw error
    }
  }

  waitForRemoteActionCompletion(actionId) {
    /* Tope de espera: si la máquina está apagada la acción se queda PENDING para siempre y
       antes el modal giraba sin fin. La acción NO se cancela, se ejecutará cuando arranque. */
    const deadline = Date.now() + 180000

    return new Promise((resolve, reject) => {
      const checkStatus = async () => {
        if (this.unmounted) return
        if (Date.now() > deadline) {
          reject(new Error('La máquina no ha respondido. La acción sigue pendiente y se ejecutará cuando vuelva a conectarse.'))
          return
        }
        try {
          const response = await this.props.fetchRemoteActions(this.props.machine.Serial)
          /* apiMiddleware nunca rechaza: un 502 o un corte de red resuelven {error} SIN
             `response`. Un solo fallo así abortaba toda la espera (y el operador veía un
             TypeError) mientras la máquina completaba el alquiler. Se trata como
             "todavía no hay respuesta" y lo acota el deadline de arriba. */
          const list = response && response.response
          if (!Array.isArray(list)) {
            setTimeout(checkStatus, 1000)
            return
          }
          const action = list.find(a => a._id === actionId)
          if (action && (action.status === 'COMPLETE' || action.status === 'ERROR')) {
            resolve(action)
          } else {
            /* ONPROCESS trae el progreso real (Scanning / Connecting / Connected en el
               alquiler RFID), así que lo mostramos en vez de un spinner mudo. */
            if (action && action.status === 'ONPROCESS' && action.result) {
              this.setState({ progress: action.result })
            }
            setTimeout(checkStatus, 1000)
          }
        } catch (error) {
          reject(error)
        }
      }
      checkStatus()
    })
  }

  selectAction(code) {
    this.props.dispatch(change('RemoteActionSingleAddForm', 'code', code))
    /* Las acciones con dato extra completan arguments en setUuid/setPhone; aquí solo la
       matrícula para que un envío sin rellenar el campo no llegue a la máquina a medias. */
    const meta = ACTION_META[code]
    const needsInput = meta && (meta.uuidInput || meta.phoneInput)
    this.props.dispatch(change('RemoteActionSingleAddForm', 'arguments', needsInput ? '' : `${this.props.lockerMatricula}`))
    this.setState({ pendingAction: code, uuid: '', phone: '', errorMessage: '', progress: '' })
  }

  cancelAction() {
    this.props.dispatch(change('RemoteActionSingleAddForm', 'code', ''))
    this.props.dispatch(change('RemoteActionSingleAddForm', 'arguments', ''))
    this.setState({ pendingAction: null, uuid: '', phone: '', errorMessage: '', progress: '' })
  }

  /* NO se recorta a 14: si un UID de 10 bytes (20 hex, ISO 14443 triple) se recortase aquí,
     pasaría el regex, la cerradura lo aceptaría recortado y la pulsera del cliente —que
     presenta el UID entero— no abriría nunca. Se deja entero para que el regex lo rechace. */
  setUuid(value) {
    const uuid = (value || '').replace(/[^0-9A-Fa-f]/g, '').toUpperCase().slice(0, 32)
    this.setState({ uuid })
    this.props.dispatch(change('RemoteActionSingleAddForm', 'arguments',
      UUID_RE.test(uuid) ? `${this.props.lockerMatricula}|${uuid}` : ''))
  }

  /* El teléfono se guarda en la máquina como prefijo+número pegados ("+34600123456"),
     exactamente igual que lo escribe el kiosco en Phone.qml. Mismo criterio que arriba: se
     recorta muy por encima del máximo válido para que un número pegado de más falle el
     regex en vez de convertirse en otro número distinto que parece correcto. */
  setPhone(value, prefixOverride) {
    const phone = (value !== null ? value : this.state.phone).replace(/\D/g, '').slice(0, 24)
    const phonePrefix = prefixOverride || this.state.phonePrefix
    this.setState({ phone, phonePrefix })
    this.props.dispatch(change('RemoteActionSingleAddForm', 'arguments',
      PHONE_RE.test(phone) ? `${this.props.lockerMatricula}|${phonePrefix}${phone}` : ''))
  }

  render() {
    const { error, pristine, submitting, handleSubmit, machine } = this.props
    const { loading, actionCompleted, pendingAction, errorMessage, showAdvanced, uuid, phone, progress } = this.state
    const { lockerMatricula, lockerStatus } = this.props

    /* Un locker ocupado no se puede realquilar: se perdería el teléfono o la pulsera del
       cliente que lo tiene ahora. La máquina vuelve a comprobarlo con su propia matriz. */
    const notRentable = !RENTABLE_STATUSES.includes(lockerStatus)

    /* Las máquinas anteriores al campo no lo traen y se comportan como PIN, así que
       cualquier valor que no sea RFID cuenta como PIN (mismo criterio que
       utils/machines.js getQuickAccessMode y que las cards Pines / RFID del modal). */
    const machineMode = machine && machine.QuickAccessMode === 'RFID' ? 'RFID' : 'PIN'

    if (loading) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '24px 16px', gap: 12 }}>
          <span style={{ color: '#64748b', fontWeight: 600, fontSize: 14 }}>Ejecutando acción...</span>
          <LoadingProgress logoHeight={36} className="compact" />
          {progress && <span style={{ color: '#94a3b8', fontSize: 12, fontFamily: 'monospace' }}>{progress}</span>}
        </div>
      )
    }

    if (actionCompleted) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: 16 }}>
          <div className="alert alert-success w-100">Proceso completado</div>
          <div className="alert alert-info w-100">Actualizando...</div>
        </div>
      )
    }

    /* Hidden fields always rendered so redux-form tracks values */
    const hiddenFields = (
      <div style={{ display: 'none' }}>
        <Field name="code" component={renderSelectField}>
          <option value="">-</option>
          <option value="SINGLE_DOOR_OPEN">Abrir</option>
          <option value="SINGLE_UNRENT">Desalquilar</option>
          <option value="SINGLE_BLOCKLATER">Reserva QR/PDA</option>
          <option value="SINGLE_RESET_PIN">Cambiar Pin</option>
          <option value="SINGLE_RENT_PIN">Alquilar PIN</option>
          <option value="SINGLE_RENT_RFID">Alquilar RFID</option>
          <option value="SINGLE_NIGHTSCAN">Vincular Individual</option>
          <option value="SINGLE_KERONG_PROGRAM_OTA">OTA Programa Individual</option>
          <option value="SINGLE_KERONG_BOOTLOADER_OTA">OTA Bootloader Individual</option>
          <option value="SINGLE_FACTORY_RESET">Reset de fábrica Individual</option>
          <option value="SINGLE_MEMORY_DEBUG">Lectura de memória en Debug</option>
        </Field>
        <Field name="arguments" type="text" component={renderInputField} placeholder="Argumentos" />
      </div>
    )

    const meta = pendingAction ? ACTION_META[pendingAction] : null
    const uuidValid = UUID_RE.test(uuid)
    const phoneValid = PHONE_RE.test(phone)
    const inputReady = !meta || (!meta.uuidInput && !meta.phoneInput) ||
      (meta.uuidInput && uuidValid) || (meta.phoneInput && phoneValid)

    const { phonePrefix, prefixOpen, prefixSearch } = this.state
    const selectedCountry = PHONE_PREFIXES.find(p => p.dial === phonePrefix) || PHONE_PREFIXES[0]

    if (pendingAction && meta) {
      return (
        <div>
          {hiddenFields}
          <div style={{ borderRadius: 10, overflow: 'hidden', border: `2px solid ${meta.accentColor}`, boxShadow: `0 4px 20px ${meta.accentColor}33` }}>
            {/* Header */}
            <div style={{ background: meta.bgGradient, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <i className={meta.iconClass} style={{ fontSize: 20, color: '#fff' }}></i>
              <div>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>{meta.label}</div>
                <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, marginTop: 2 }}>
                  Locker <strong style={{ color: '#fff' }}>{machine ? Number(lockerMatricula) + machine.FirstIndex - 1 : lockerMatricula}</strong> &mdash; {machine && machine.Name}
                </div>
              </div>
            </div>
            {/* Body */}
            <div style={{ background: '#fff', padding: '20px' }}>
              <p style={{ color: '#374151', fontSize: 14, lineHeight: 1.6, margin: '0 0 14px' }}>
                {meta.description}
              </p>
              {meta.warning && (
                <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <i className="fas fa-exclamation-triangle" style={{ color: '#d97706', marginTop: 2, flexShrink: 0 }}></i>
                  <span style={{ color: '#92400e', fontSize: 13, lineHeight: 1.5 }}>{meta.warning}</span>
                </div>
              )}
              {meta.uuidInput && (
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
                    UUID de la tarjeta
                  </label>
                  <input
                    type="text"
                    autoFocus
                    value={uuid}
                    onChange={e => this.setUuid(e.target.value)}
                    placeholder="04A1B2C3"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${uuid && !uuidValid ? '#fca5a5' : '#d1d5db'}`, fontSize: 15, fontFamily: 'monospace', letterSpacing: 2, textTransform: 'uppercase' }}
                  />
                  <div style={{ fontSize: 11, color: uuid && !uuidValid ? '#dc2626' : '#94a3b8', marginTop: 5 }}>
                    8 o 14 caracteres hexadecimales (4 o 7 bytes).
                  </div>
                </div>
              )}
              {meta.phoneInput && (
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
                    Teléfono del cliente
                  </label>
                  {/* El desplegable va en flujo normal, debajo de la fila, y NO absoluto:
                      .lm-card lleva overflow:hidden, así que un dropdown posicionado se
                      vería recortado a dos filas. Así el panel simplemente crece. */}
                  <div ref={this.prefixRef}>
                    <div className="lm-phone-row">
                      <div className="lm-prefix-picker">
                        <button
                          type="button"
                          className="lm-prefix-trigger"
                          onClick={() => this.setState(s => ({ prefixOpen: !s.prefixOpen }))}
                        >
                          <ReactCountryFlag countryCode={selectedCountry.code} svg style={{ width: '1.4em', height: '1.4em', borderRadius: 3 }} />
                          <span>{selectedCountry.dial}</span>
                          <i className={`fas fa-chevron-${prefixOpen ? 'up' : 'down'}`} style={{ fontSize: '0.6rem', opacity: 0.6 }}></i>
                        </button>
                      </div>
                      <input
                        className="lm-phone-number"
                        type="tel"
                        autoFocus
                        placeholder="600 000 000"
                        value={phone}
                        onChange={e => this.setPhone(e.target.value)}
                      />
                    </div>
                    {prefixOpen && (
                      <div className="lm-prefix-dropdown">
                        <input
                          type="text"
                          className="lm-prefix-search"
                          placeholder="Buscar país..."
                          value={prefixSearch}
                          onChange={e => this.setState({ prefixSearch: e.target.value })}
                          onClick={e => e.stopPropagation()}
                          autoFocus
                        />
                        {PHONE_PREFIXES.filter(p => {
                          const s = prefixSearch.toLowerCase()
                          return !s || p.name.toLowerCase().includes(s) || p.dial.includes(s) || p.code.toLowerCase().includes(s)
                        }).map(p => (
                          <div
                            key={p.code}
                            className={`lm-prefix-option${p.dial === phonePrefix ? ' selected' : ''}`}
                            onClick={() => {
                              this.setState({ prefixOpen: false, prefixSearch: '' })
                              this.setPhone(null, p.dial)
                            }}
                          >
                            <ReactCountryFlag countryCode={p.code} svg style={{ width: '1.3em', height: '1.3em', borderRadius: 3 }} />
                            <span className="lm-prefix-dial">{p.dial}</span>
                            <span className="lm-prefix-name">{p.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: phone && !phoneValid ? '#dc2626' : '#94a3b8', marginTop: 5 }}>
                    Se guardará como <strong>{phonePrefix}{phone || '…'}</strong> — mínimo 6 dígitos.
                  </div>
                </div>
              )}
              {(error || errorMessage) && <div className="alert alert-danger mb-3">{error || errorMessage}</div>}
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type="button"
                  onClick={this.cancelAction}
                  style={{ padding: '11px 22px', borderRadius: 8, border: '1px solid #d1d5db', background: '#f9fafb', color: '#374151', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSubmit(this.submit)}
                  disabled={!inputReady}
                  style={{ flex: 1, padding: '11px 0', borderRadius: 8, border: 'none', background: inputReady ? meta.accentColor : '#cbd5e1', color: '#fff', fontWeight: 700, fontSize: 14, cursor: inputReady ? 'pointer' : 'not-allowed' }}
                >
                  <i className={`${meta.iconClass} mr-2`}></i>{meta.confirmText}
                </button>
              </div>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div>
        {hiddenFields}

        {/* Operative actions */}
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Operativas</div>
          <div className="action-grid-2">
            {OPERATIVE_ACTIONS.map(code => {
              const m = ACTION_META[code]
              return (
                <button
                  key={code}
                  type="button"
                  onClick={() => this.selectAction(code)}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '14px 8px', borderRadius: 8, border: 'none', background: m.bgGradient, color: '#fff', fontWeight: 700, fontSize: 11, cursor: 'pointer', letterSpacing: 0.3, textTransform: 'uppercase' }}
                >
                  <i className={m.iconClass} style={{ fontSize: 18 }}></i>
                  {m.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* Rent actions */}
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Alquiler</div>
          <div className="action-grid-2">
            {RENT_ACTIONS.map(code => {
              const m = ACTION_META[code]
              /* Cada alquiler sólo tiene sentido en su modo: un teléfono no abre nada en una
                 máquina de pulsera y una pulsera no se entrega en una de PIN. */
              const wrongMode = m.requiresMode !== machineMode
              const blocked = notRentable || wrongMode
              return (
                <button
                  key={code}
                  type="button"
                  disabled={blocked}
                  title={wrongMode
                    ? `Solo disponible en modo ${m.requiresMode === 'RFID' ? 'PULSERA RFID' : 'PIN'}`
                    : notRentable ? 'El locker no está libre. Sólo se puede alquilar en AV o FB.' : m.label}
                  onClick={() => this.selectAction(code)}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '14px 8px', borderRadius: 8, border: 'none', background: blocked ? '#e2e8f0' : m.bgGradient, color: blocked ? '#94a3b8' : '#fff', fontWeight: 700, fontSize: 11, cursor: blocked ? 'not-allowed' : 'pointer', letterSpacing: 0.3, textTransform: 'uppercase' }}
                >
                  <i className={m.iconClass} style={{ fontSize: 18 }}></i>
                  {m.label}
                </button>
              )
            })}
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 5 }}>
            <i className="fas fa-info-circle mr-1"></i>
            {notRentable
              ? `El locker no está libre (${lockerStatus || '—'}). Sólo se puede alquilar cuando está en AV o FB.`
              : `Máquina en modo ${machineMode === 'RFID' ? 'PULSERA RFID' : 'PIN'}: sólo se puede alquilar por ${machineMode === 'RFID' ? 'pulsera' : 'teléfono'}.`}
          </div>
        </div>

        {/* Critical actions */}
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Críticas</div>
          <div className="action-grid-3">
            {CRITICAL_ACTIONS.map(code => {
              const m = ACTION_META[code]
              return (
                <button
                  key={code}
                  type="button"
                  onClick={() => this.selectAction(code)}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '12px 6px', borderRadius: 8, border: '1px solid #fca5a5', background: '#fff1f2', color: '#dc2626', fontWeight: 700, fontSize: 10, cursor: 'pointer', letterSpacing: 0.3, textTransform: 'uppercase' }}
                >
                  <i className={m.iconClass} style={{ fontSize: 16 }}></i>
                  {m.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* Advanced / custom action */}
        <div>
          <button
            type="button"
            onClick={() => this.setState(s => ({ showAdvanced: !s.showAdvanced }))}
            style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 11, fontWeight: 600, cursor: 'pointer', padding: '4px 0', display: 'flex', alignItems: 'center', gap: 4 }}
          >
            <i className={`fas fa-chevron-${showAdvanced ? 'up' : 'down'}`} style={{ fontSize: 9 }}></i>
            Acción personalizada
          </button>
          {showAdvanced && (
            <form onSubmit={handleSubmit(this.submit)} style={{ marginTop: 8, padding: 12, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <Field name="code" component={renderSelectField}>
                <option value="">Selecciona una acción...</option>
                <option value="REBOOT">Reiniciar máquina</option>
                <option value="SINGLE_DOOR_OPEN">Abrir</option>
                <option value="SINGLE_UNRENT">Desalquilar</option>
                <option value="SINGLE_BLOCKLATER">Reserva QR/PDA</option>
                <option value="SINGLE_RESET_PIN">Cambiar Pin</option>
                <option value="SINGLE_RENT_PIN">Alquilar PIN</option>
                <option value="SINGLE_RENT_RFID">Alquilar RFID</option>
                <option value="SINGLE_NIGHTSCAN">Vincular Individual</option>
                <option value="SINGLE_KERONG_PROGRAM_OTA">OTA Programa Individual</option>
                <option value="SINGLE_KERONG_BOOTLOADER_OTA">OTA Bootloader Individual</option>
                <option value="SINGLE_FACTORY_RESET">Reset de fábrica Individual</option>
                <option value="SINGLE_MEMORY_DEBUG">Lectura de memória en Debug</option>
              </Field>
              <Field name="arguments" type="text" component={renderInputField} placeholder="Argumentos" />
              <button
                className="btn bg-vw-light text-white w-100 mt-2"
                type="submit"
                disabled={pristine || submitting}
              >
                <i className="fas fa-bolt mr-1"></i>Ejecutar
              </button>
              {error && <div className="alert alert-danger mt-2">{error}</div>}
            </form>
          )}
        </div>
      </div>
    )
  }
}

RemoteActionSingleAddForm = reduxForm({
  form: 'RemoteActionSingleAddForm',
})(RemoteActionSingleAddForm)

const mapStateToProps = state => ({
  auth: state.auth,
  role: state.auth.role,
  type: state.auth.type,
  fullname: state.auth.fullname,
})

const mapDispatchToProps = dispatch => ({
  addRemoteAction: bindActionCreators(addRemoteAction, dispatch),
  fetchRemoteActions: bindActionCreators(fetchRemoteActions, dispatch),
  dispatch,
})

export default connect(mapStateToProps, mapDispatchToProps)(RemoteActionSingleAddForm)

